export type BudgetAverageLike = {
  budgetCategoryId: number;
  categoryName: string;
  plaidPrimary: string;
  monthlyAverage: number;
};

export type BudgetEntryLike = {
  budgetCategoryId: number;
  amount: number;
  categoryName: string;
  categoryType?: string | null;
  plaidPrimary?: string | null;
  entryKey?: string;
  isDerived?: boolean;
  isEditable?: boolean;
  derivedSourceLabel?: string | null;
};

export type BudgetCategoryCatalogLike = {
  id: number;
  name: string;
  type?: string | null;
  plaidPrimary?: string | null;
  plaidDetailed?: string | null;
};

export type BudgetDisplayCategory = {
  displayId: string;
  budgetCategoryId: number;
  categoryName: string;
  categoryType: string | null;
  plaidPrimary: string;
  monthlyAverage: number;
  entryAmount: number | null;
  isDerived: boolean;
  isEditable: boolean;
  derivedSourceLabel: string | null;
};

function normalizeBudgetAmount(amount: number): number {
  if (!Number.isFinite(amount)) return 0;
  return Math.max(amount, 0);
}

const TAX_PAYMENT_DETAILED = 'GOVERNMENT_AND_NON_PROFIT_TAX_PAYMENT';

export function buildBudgetDisplayCategories(
  averages: BudgetAverageLike[],
  entries: BudgetEntryLike[],
  categoryCatalog: BudgetCategoryCatalogLike[] = [],
): BudgetDisplayCategory[] {
  const averageMap = new Map<number, BudgetAverageLike>();
  for (const average of averages) {
    averageMap.set(average.budgetCategoryId, average);
  }

  const categories: BudgetDisplayCategory[] = [];

  for (const entry of entries) {
    const average = averageMap.get(entry.budgetCategoryId);
    categories.push({
      displayId: entry.entryKey ?? `${entry.isDerived ? 'derived' : 'manual'}-${entry.budgetCategoryId}`,
      budgetCategoryId: entry.budgetCategoryId,
      categoryName: entry.derivedSourceLabel ?? entry.categoryName ?? average?.categoryName ?? `Category ${entry.budgetCategoryId}`,
      categoryType: entry.categoryType ?? 'expense',
      plaidPrimary: entry.plaidPrimary ?? average?.plaidPrimary ?? 'OTHER',
      monthlyAverage: normalizeBudgetAmount(Number(average?.monthlyAverage ?? 0)),
      entryAmount: normalizeBudgetAmount(Number(entry.amount)),
      isDerived: entry.isDerived ?? false,
      isEditable: entry.isEditable ?? !(entry.isDerived ?? false),
      derivedSourceLabel: entry.derivedSourceLabel ?? null,
    });
  }

  const seenCategoryIds = new Set(entries.map((entry) => entry.budgetCategoryId));
  for (const average of averages) {
    if (seenCategoryIds.has(average.budgetCategoryId)) continue;
    categories.push({
      displayId: `average-${average.budgetCategoryId}`,
      budgetCategoryId: average.budgetCategoryId,
      categoryName: average.categoryName,
      categoryType: 'expense',
      plaidPrimary: average.plaidPrimary,
      monthlyAverage: normalizeBudgetAmount(Number(average.monthlyAverage)),
      entryAmount: null,
      isDerived: false,
      isEditable: true,
      derivedSourceLabel: null,
    });
  }

  const seenDisplayCategoryIds = new Set(
    categories.map((category) => category.budgetCategoryId),
  );
  for (const category of categoryCatalog) {
    if (seenDisplayCategoryIds.has(category.id)) continue;
    if (category.plaidDetailed === TAX_PAYMENT_DETAILED) continue;
    categories.push({
      displayId: `catalog-${category.id}`,
      budgetCategoryId: category.id,
      categoryName: category.name,
      categoryType: category.type ?? 'expense',
      plaidPrimary: category.plaidPrimary ?? 'OTHER',
      monthlyAverage: 0,
      entryAmount: null,
      isDerived: false,
      isEditable: true,
      derivedSourceLabel: null,
    });
  }

  return categories;
}
